import { Button, ConfirmationModal, ToastVariant } from "@factorearth/component-library";
import { Colors } from "@factorearth/component-library/dist/Theme/types";
import { FormTemplate, FormTemplateForResolver, UserForResolver } from "db/definitions";
import { getAllFormTemplates as getAllOfflineFormTemplates } from "db/queries/formTemplateQueries";
import useAutoSync from "hooks/useAutoSync";
import useProjectConfig from "hooks/useProjectConfig";
import React, { useEffect, useMemo, useState } from "react";
import { FiDownloadCloud, FiFilter, FiUploadCloud } from "react-icons/fi";
import { syncSelectedTemplatesOffline, syncSelectedTemplatesOnline, uninstallSelectedTemplates } from "utils/templateSyncModules";
import TemplateListSyncItem from "./TemplateSyncListItem/TemplateListSyncItem.jsx";
import styles from "./TemplateSyncMenu.module.scss";
import { recordSentry } from "utils/recordSentry.js";

interface Props {
	color: Colors;
	formTemplates: FormTemplate[];
	userDoc: UserForResolver;
}

interface FiltersObj {
	onlyAutoSynced: boolean;
	onlyOffline: boolean;
	onlyTheseFormTemplateGroups: string;
	sortBy: keyof FormTemplate | "";
}
interface ToastContent {
	title: string;
	message: string;
	type: ToastVariant;
}

function TemplateSyncMenu(props: Props) {
	const { color, formTemplates, userDoc } = props;

	const filtersObj: FiltersObj = {
		onlyOffline: false,
		onlyAutoSynced: false,
		onlyTheseFormTemplateGroups: "",
		sortBy: "",
	};

	const [templates, setTemplates] = useState([...formTemplates]);
	const [offlineTemplates, setOfflineTemplates] = useState<FormTemplateForResolver[]>([]);
	const [openToast, setOpenToast] = useState(false);
	const [toastContent, setToastContent] = useState<ToastContent>({
		title: "",
		message: "",
		type: "success",
	});
	const [loading, setLoading] = useState(false);
	const [filters, setFilters] = useState(filtersObj);
	const [filterMenuOpen, setFilterMenuOpen] = useState(false);
	const [selected, setSelected] = useState<FormTemplate[]>([]);
	const [rerender, setRerender] = useState(false);

	const { projectConfigs } = useProjectConfig(userDoc);

	const { autoSyncConfigs, addTemplatesToAutoSync, removeTemplatesFromAutoSync } = useAutoSync(projectConfigs);

	useEffect(() => {
		setTemplates([...formTemplates]);
	}, [formTemplates]);

	useEffect(() => {
		const setUpOfflineTemplates = async () => {
			const offlineData = await getAllOfflineFormTemplates();
			setOfflineTemplates(offlineData);
		};
		setUpOfflineTemplates();
	}, [rerender]);

	const syncSelectedToDevice = async () => {
		if (selected.length > 0) {
			try {
				setLoading(true);
				const allSelected = selected;
				setToastContent({
					title: "Saved Templates",
					message: "Syncing the selected form templates.",
					type: "info",
				});
				setOpenToast(true);
				await syncSelectedTemplatesOffline(allSelected);
				await addTemplatesToAutoSync(allSelected);
				setSelected([]);
				setRerender(!rerender);
				setToastContent({
					title: "Saved Templates",
					message: "You have successfully saved and auto-synced the selected form templates.",
					type: "success",
				});
				setOpenToast(true);
			} catch (err) {
				recordSentry({ error: err, msg: "Error syncing templates to device" });
			}
		} else {
			setToastContent({
				title: "No Templates Selected",
				message: "You didn't select any templates to save!",
				type: "destructive",
			});
			setOpenToast(true);
		}
		setLoading(false);
	};

	async function deleteSelectedFromDevice() {
		if (selected.length > 0) {
			setLoading(true);
			const allSelected = selected;
			setToastContent({
				title: "Deleted Templates",
				message: "Deleting the selected form templates.",
				type: "info",
			});
			setOpenToast(true);
			await uninstallSelectedTemplates(allSelected);
			await removeTemplatesFromAutoSync(allSelected);
			const finalOffline = [...offlineTemplates].filter((template) => !selected.some((ft) => ft.id !== template.id));
			setOfflineTemplates(finalOffline);
			setSelected([]);
			setRerender(!rerender);
			setToastContent({
				title: "Deleted Templates",
				message: "You have successfully deleted the selected form templates and updates will not be auto-synced.",
				type: "success",
			});
			setOpenToast(true);
		} else {
			setToastContent({
				title: "No Templates Selected",
				message: "You didn't select any templates to uninstall!",
				type: "destructive",
			});
			setOpenToast(true);
		}
		setLoading(false);
	}

	const syncSelectedToCloud = async () => {
		if (selected.length === 0) {
			setToastContent({
				title: "No Templates Selected",
				message: "You didn't select any templates to save!",
				type: "destructive",
			});
			setOpenToast(true);
			return;
		}
		setLoading(true);
		const allSelected = selected;
		setToastContent({
			title: "Saved Templates",
			message: "Syncing the selected form templates.",
			type: "info",
		});
		setOpenToast(true);
		await syncSelectedTemplatesOnline(allSelected);
		setSelected([]);
		setRerender(!rerender);
		setToastContent({
			title: "Saved Templates",
			message: "You have successfully saved the selected form templates.",
			type: "success",
		});
		setOpenToast(true);
		setLoading(false);
	};

	const autoSyncSelected = async () => {
		if (selected.length === 0) {
			setToastContent({
				title: "No Templates Selected",
				message: "You didn't select any templates to autosync!",
				type: "destructive",
			});
			setOpenToast(true);
			return;
		}
		setLoading(true);
		const allSelected = selected;
		setToastContent({
			title: "Auto Sync Templates",
			message: "Adding the selected form templates to autosyncing.",
			type: "info",
		});
		setOpenToast(true);
		await addTemplatesToAutoSync(allSelected);
		setSelected([]);
		setToastContent({
			title: "Auto Sync Templates",
			message: "You have successfully added the selected form templates to the autosyncing.",
			type: "success",
		});
		setOpenToast(true);
		setLoading(false);
	};

	const disableAutoSyncOnSelected = async () => {
		if (selected.length === 0) {
			setToastContent({
				title: "No Templates Selected",
				message: "You didn't select any templates to disable autosync!",
				type: "destructive",
			});
			setOpenToast(true);
			return;
		}
		setLoading(true);
		const allSelected = selected;
		setToastContent({
			title: "Auto Sync Templates",
			message: "Unsyncing the selected form templates from autosyncing.",
			type: "info",
		});
		setOpenToast(true);
		await removeTemplatesFromAutoSync(allSelected);
		setSelected([]);
		setToastContent({
			title: "Auto Sync Templates",
			message: "You have successfully unsynced the selected form templates from autosyncing.",
			type: "success",
		});
		setOpenToast(true);
		setLoading(false);
	};

	function toggleFilterMenu() {
		setFilterMenuOpen(!filterMenuOpen);
	}

	function handleSelect(selectedTemplate: FormTemplate) {
		const selectedCopy = [...selected];
		const index = selectedCopy.findIndex((formTemplate) => formTemplate.id === selectedTemplate.id);

		// const indexOf = selectedCopy.indexOf(selectedTemplate);
		if (index === -1) {
			selectedCopy.push(selectedTemplate);
		} else {
			selectedCopy.splice(index, 1);
		}

		setSelected(selectedCopy);
	}

	function formTemplateGroupsList(templates: FormTemplate[]) {
		const groups: string[] = [];
		for (let i = 0; i < templates.length; i++) {
			if (!groups.includes(templates[i].formTemplateGroup)) {
				groups.push(templates[i].formTemplateGroup);
			}
		}

		return groups;
	}

	function buildFilterMenu() {
		return (
			<div
				className={styles.filterMenuWrapper}
				style={{
					background: color.background.primary,
					color: color.text.primary,
				}}>
				<div className={styles.miniFilterHeadWrapper}>
					<div className={styles.TSMButton}>
						<Button
							colorPalette={color}
							id="view-only-saved-locally"
							label={filters.onlyOffline ? "View All Templates" : "View Locally Saved Templates Only"}
							variant="filled"
							onClick={() => {
								const updatedFilters = {
									...filters,
									onlyOffline: !filters.onlyOffline,
								};
								setFilters(updatedFilters);
							}}
						/>
					</div>
					{filters.onlyOffline && (
						<>
							<div className={styles.TSMButton}>
								<Button
									variant="filled"
									colorPalette={color}
									id="autosync"
									onClick={autoSyncSelected}
									disabled={selected.length === 0 || loading}>
									Auto Sync Selected Templates
								</Button>
							</div>
							<div className={styles.TSMButton}>
								<Button
									variant="filled"
									colorPalette={color}
									id="disable-autosync"
									onClick={disableAutoSyncOnSelected}
									disabled={selected.length === 0 || loading}>
									Disable Auto Sync on Selected Templates
								</Button>
							</div>
							<div className={styles.TSMButton}>
								<Button
									variant="destructive"
									colorPalette={color}
									id="uninstall"
									onClick={deleteSelectedFromDevice}
									disabled={selected.length === 0 || loading}>
									Uninstall Selected Templates
								</Button>
							</div>
						</>
					)}
				</div>
				<div className={styles.miniFilterWrapper}>
					<p>Filter by Form Template Groups:</p>
					<select
						id="dropdown-form-groups"
						className={styles.TSMDropdown}
						style={{
							background: color.background.secondary,
							color: color.text.tertiary,
						}}
						onChange={(e) => {
							const updatedFilters = {
								...filters,
								onlyTheseFormTemplateGroups: e.target.value,
							};
							setFilters(updatedFilters);
						}}>
						<option value="">All</option>
						{formTemplateGroupsList(templates).map((group) => (
							<option key={`template_sync_menu_option${group}`} value={group}>
								{group}
							</option>
						))}
					</select>
				</div>
			</div>
		);
	}

	function organizeTemplatesByTemplateKey(unorganizedTemplates: FormTemplate[]) {
		// need to cahnge this to organize by form template Group and put in some separate styling for that.
		//so organizedTemplates will now have formTemplateGroup as the key then each child within that
		// Organizing by {formTemplateGroup: { template/key: [templates]...}}
		const organizedTemplateMap: Record<string, Record<string, FormTemplate[]>> = {};
		for (let i = 0; i < unorganizedTemplates.length; i++) {
			const formTemplate = unorganizedTemplates[i];
			const formTemplateGroup = formTemplate.formTemplateGroup;
			const currentType = formTemplate.template;
			const current = formTemplate;
			if (organizedTemplateMap[formTemplateGroup]) {
				// form template group is already in there
				if (organizedTemplateMap[formTemplateGroup][currentType]) {
					// if it hits this part then the template (category) has been added, so this template just needs
					// to be pushed in the group
					organizedTemplateMap[formTemplateGroup][currentType].push(current);
				} else {
					// it hasnt been added, so ths needs to be initialized with an array with this one template in it.
					organizedTemplateMap[formTemplateGroup][currentType] = [current];
				}
			} else {
				// formTemplateGroup is not in there, so all freshys
				organizedTemplateMap[formTemplateGroup] = {
					[currentType]: [current],
				};
			}
		}

		return organizedTemplateMap;
	}

	const listOfTemplates = useMemo(() => {
		const offlineTemplatesMap: Record<string, FormTemplateForResolver> = {};

		for (let i = 0; i < offlineTemplates.length; i++) {
			offlineTemplatesMap[offlineTemplates[i].id] = offlineTemplates[i];
		}

		const filterList = (templatesToFilter: FormTemplate[]) => {
			let templatesMap = templatesToFilter;
			// go back an use the funcitonality in sort.ts to accomplish the goals of the function below:
			function sortByKey(array: FormTemplate[], key: keyof FormTemplate) {
				const obj: Record<string, FormTemplate[]> = {};

				for (let i = 0; i < array.length; i++) {
					const objKey = array[i][key];
					if (typeof objKey === "string") {
						if (obj[objKey]) {
							obj[objKey].push(array[i]);
						} else {
							obj[objKey] = [array[i]];
						}
					}
				}

				const finalArray = [...Object.values(obj)].flat().sort((a, b) => {
					const valueA = a[key];
					const valueB = b[key];
					if (valueA === typeof "string" && valueB === typeof "string") {
						if (valueA < valueB) {
							return -1;
						}
						if (valueA > valueB) {
							return 1;
						}
					}
					return 0;
				});

				return finalArray;
			}

			if (filters.onlyOffline) {
				templatesMap = templatesMap.filter((template) => offlineTemplatesMap[template.id]);
			}

			if (filters.onlyTheseFormTemplateGroups) {
				templatesMap = templatesMap.filter((template) => template.formTemplateGroup === filters.onlyTheseFormTemplateGroups);
			}

			if (filters.sortBy) {
				templatesMap = sortByKey(templatesMap, filters.sortBy);
			}
			return templatesMap;
		};

		const buildList = (templatesToUse: FormTemplate[]) => {
			const finalTemplates = filterList(templatesToUse);
			let selectedSet: Set<string> = new Set();
			for (let i = 0; i < selected.length; i++) {
				const selectedItem = selected[i];
				selectedSet.add(selectedItem.id);
			}
			const organizedTemplates = organizeTemplatesByTemplateKey(finalTemplates);
			const listToReturn = [];

			const formTemplateGroups = Object.keys(organizedTemplates).sort(sortTemplateGroups);

			for (let i = 0; i < formTemplateGroups.length; i++) {
				const formTemplateGroup = formTemplateGroups[i];

				const formTemplateGroupObj = organizedTemplates[formTemplateGroup];
				const templateKeys = Object.keys(formTemplateGroupObj);
				let groupedFormTemplates = [];
				let groupedFormTemplatesData = [];
				for (let ii = 0; ii < templateKeys.length; ii++) {
					const currentKey = templateKeys[ii];
					// Need to pick the "default" version of the template.
					let currentTemplate = formTemplateGroupObj[currentKey][0];
					for (let iii = 0; iii < formTemplateGroupObj[currentKey].length; iii++) {
						const potentialDefaultTemplate = formTemplateGroupObj[currentKey][iii];
						if (
							!potentialDefaultTemplate.version ||
							potentialDefaultTemplate.version === "" ||
							(potentialDefaultTemplate.version &&
								(potentialDefaultTemplate.versionName === "default" || potentialDefaultTemplate.versionName === ""))
						) {
							currentTemplate = potentialDefaultTemplate;
						}
					}

					const offline = offlineTemplatesMap[currentTemplate.id] ? true : false;
					let autoSynced;

					if (offline && autoSyncConfigs[currentTemplate.template]) {
						const autoSyncConfig = autoSyncConfigs[currentTemplate.template];
						const idIndex = autoSyncConfig.versionIds.indexOf(currentTemplate.id);
						if (idIndex === -1) {
							autoSynced = false;
						} else {
							autoSynced = true;
						}
					} else {
						autoSynced = false;
					}

					// changing this so were are passing the whole array of temapltes for the current templateKey
					// also will need to pass the remainging templates to the TemplateListSyncItem so that it can build the mini list using them - could just pass the whole thing and handle removing the first

					const listItem = (
						<TemplateListSyncItem
							key={`template-sync-menu-list-item-${currentTemplate.id}`}
							formTemplate={currentTemplate}
							isOffline={offline}
							isAutoSyncing={autoSynced}
							color={color}
							handleSelect={handleSelect}
							checked={selectedSet.has(currentTemplate.id) ? true : false}
							selectedSet={selectedSet}
							offlineTemplatesMap={offlineTemplatesMap}
							additionalTemplates={formTemplateGroupObj[currentKey]}
						/>
					);

					groupedFormTemplates.push(listItem);
					groupedFormTemplatesData.push(currentTemplate);
				}

				const wholeFormTemplateGroupSelected = determineIfWholeFormTemplateGroupIsSelected(groupedFormTemplatesData);
				const sortedTemplates = groupedFormTemplates.sort(sortTemplates);
				let allTemplates: FormTemplate[] = [];
				const valuesArray = Object.values(formTemplateGroupObj);
				for (let ii = 0; ii < valuesArray.length; ii++) {
					const arrayOfThings = valuesArray[ii];
					allTemplates = allTemplates.concat(arrayOfThings);
				}
				const groupedHtml = (
					<div className={styles.form_template_group_list} key={`form-template-group-${formTemplateGroup}`}>
						<div className={styles.form_template_group_list_item_header_wrapper} style={{ background: color.background.secondary }}>
							{/* <label htmlFor={`select-all-${formTemplateGroup}`}>All: {formTemplateGroup}</label> */}
							<input
								className={styles.form_template_group_check}
								type="checkbox"
								id={`select-all-${formTemplateGroup}`}
								checked={wholeFormTemplateGroupSelected}
								onChange={() => handleSelectWholeFormTemplateGroup(allTemplates)}
							/>
							<div className={styles.form_template_group_list_item_header}>{formTemplateGroup}</div>
						</div>

						<div className={styles.form_template_group_list_items}>{sortedTemplates}</div>
					</div>
				);

				listToReturn.push(groupedHtml);
			}

			return listToReturn;
		};

		return <>{templates.length > 0 ? buildList(templates) : <div>ehh</div>}</>;
	}, [offlineTemplates, filters, selected, templates]);

	/**
	 * callback for sort method for form templates
	 * @param {Object} a FormTemplate a
	 * @param {*} b FormTemplate b
	 * @returns sort direction
	 */
	function sortTemplateGroups(a: string, b: string) {
		if (a < b) {
			return -1;
		}
		if (a > b) {
			return 1;
		}

		return 0;
	}

	/**
	 * callback for sort method for form templates
	 * @param {Object} a FormTemplate a
	 * @param {*} b FormTemplate b
	 * @returns sort direction
	 */
	function sortTemplates(a: React.JSX.Element, b: React.JSX.Element) {
		if (a.props.formTemplate.template < b.props.formTemplate.template) {
			return -1;
		}
		if (a.props.formTemplate.template > b.props.formTemplate.template) {
			return 1;
		}

		return 0;
	}

	function determineIfWholeFormTemplateGroupIsSelected(wholeFormTemplateGroup: FormTemplate[]) {
		let isAllSelected = true;
		for (let i = 0; i < wholeFormTemplateGroup.length; i++) {
			const formTemplateInGroup = wholeFormTemplateGroup[i];
			let thisOneFound = false;

			for (let ii = 0; ii < selected.length; ii++) {
				const selectedFormTemplate = selected[ii];
				if (formTemplateInGroup.id === selectedFormTemplate.id) {
					thisOneFound = true;
					continue;
				}
			}

			if (!thisOneFound) {
				isAllSelected = false;
				break;
			}
		}

		return isAllSelected;
	}

	/**
	 * Handles selecting a whole form template group.
	 * @param formGroupsToAddToSelected form template groups that were selected.
	 */
	function handleSelectWholeFormTemplateGroup(formGroupsToAddToSelected: FormTemplate[]) {
		let selectedCopy = [...selected];
		// either remove all or add all depending on if they are in there or not
		const needsRemove = determineIfWholeFormTemplateGroupIsSelected(formGroupsToAddToSelected);
		if (needsRemove) {
			const formGroupsToAddMap: Record<string, FormTemplate> = {};

			for (let i = 0; i < formGroupsToAddToSelected.length; i++) {
				const formGroupToAdd = formGroupsToAddToSelected[i];
				formGroupsToAddMap[formGroupToAdd.id] = formGroupToAdd;
			}

			const newSelected = selectedCopy.filter((template) => {
				if (!formGroupsToAddMap[template.id]) {
					return template;
				}
			});

			selectedCopy = newSelected;
		} else {
			selectedCopy = selectedCopy.concat(formGroupsToAddToSelected);
		}
		setSelected(selectedCopy);
	}

	return (
		<>
			<ConfirmationModal
				colorPalette={color}
				details={toastContent.message}
				confirmTitle={toastContent.title}
				open={openToast}
				setOpen={setOpenToast}
				toast={toastContent.type}
				timeout={5000}
				canClose
			/>
			<div className={styles.templateSyncMenuWrapper} style={{ color: color.text.primary }}>
				<div style={{ color: color.text.primary }} className={styles.headerInfo}>
					<h2 className={styles.headerTitle}>Form Template Manager</h2>
					<p className={styles.headerContent}>
						Manage templates on your device, select templates to automatically update, and view details about template changes. If you need a form
						template not listed here, please contact us at devs@factorearth.com.
					</p>
				</div>
				<div className={styles.buttons_wrapper}>
					<Button icon={<FiFilter />} colorPalette={color} variant="filled" onClick={toggleFilterMenu} label="Filter" />
					<Button
						icon={<FiDownloadCloud />}
						colorPalette={color}
						onClick={syncSelectedToDevice}
						label="Sync Selected to Device"
						disabled={selected.length === 0 || loading}
						variant="filled"
					/>
					<Button
						icon={<FiUploadCloud />}
						colorPalette={color}
						variant="filled"
						onClick={syncSelectedToCloud}
						disabled={selected.length === 0 || loading}
						label="Sync Selected to Cloud"
					/>
				</div>
				{filterMenuOpen && buildFilterMenu()}
				<div className={styles.templateList}>
					{templates.length ? (
						listOfTemplates
					) : (
						<div style={{ color: color.text.primary }} className={styles.info}>
							Templates loading... This may take a few seconds
						</div>
					)}
					<div className={styles.contact_us_text}>If you need a form template not listed here, please contact us at devs@factorearth.com.</div>
				</div>
			</div>
		</>
	);
}

export default TemplateSyncMenu;
